import { useState, useEffect } from "react";
import * as React from "react";
import { useAuth } from "../contexts/AuthContext";
import { getProductCatalog, updateProductCatalog, createProductCatalog, deleteProductCatalog, cleanupMonthlyActiveTasks, undoCleanupMonthlyActiveTasks, retrofitTaskTemplates, undoRetrofitTaskTemplates } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Loader2, Plus, Trash2, GripVertical, Settings, ChevronDown, ChevronRight,
  Save, X, PenLine, Sparkles, Undo2, CheckCircle2,
} from "lucide-react";

const FORM_OPTIONS = [
  { value: "", label: "No linked form" },
  { value: "meta_facebook", label: "Facebook / Meta Intake" },
  { value: "seo_aeo", label: "SEO & AEO Intake" },
  { value: "a2p_registration", label: "A2P Registration" },
  { value: "web_design", label: "Website Questionnaire" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProductDialog, setNewProductDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCatalog = async () => {
    try {
      const { data } = await getProductCatalog();
      setCatalog(data);
    } catch (e) {
      console.error("Failed to fetch catalog:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalog(); }, []);

  const handleSaveProduct = async (product) => {
    setSaving(true);
    try {
      await updateProductCatalog(product.id, product);
      toast.success(`"${product.name}" saved`);
      setEditingProduct(null);
      fetchCatalog();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProduct = async (product) => {
    setSaving(true);
    try {
      await createProductCatalog(product);
      toast.success(`"${product.name}" created`);
      setNewProductDialog(false);
      fetchCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteProductCatalog(productId);
      toast.success("Product deleted");
      setDeleteDialog(null);
      fetchCatalog();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage products, task templates, and deadlines</p>
        </div>
        <Dialog open={newProductDialog} onOpenChange={setNewProductDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-btn"><Plus className="mr-2 h-4 w-4" />Add Product Category</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Product Category</DialogTitle></DialogHeader>
            <NewProductForm onSave={handleCreateProduct} saving={saving} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stale-task cleanup — super_admin only. Closes every pending task
          sitting on a monthly_active project (that stale checklist is
          replaced by day-to-day messaging once a client is out of
          onboarding). Fully reversible via Undo. */}
      {user?.role === "super_admin" && <StaleTaskCleanupPanel />}
      {user?.role === "super_admin" && <TaskTemplateRetrofitPanel />}

      {/* Product Cards */}
      <div className="space-y-4">
        {catalog.map((product) => (
          editingProduct === product.id ? (
            <ProductEditor
              key={product.id}
              product={product}
              onSave={handleSaveProduct}
              onCancel={() => setEditingProduct(null)}
              saving={saving}
            />
          ) : (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={() => setEditingProduct(product.id)}
              onDelete={() => setDeleteDialog(product.id)}
            />
          )
        ))}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product Category?</AlertDialogTitle>
            <AlertDialogDescription>This will remove this product category. Existing orders using it won't be affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteProduct(deleteDialog)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProductCard({ product, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const extTasks = product.external_tasks || [];
  const intTasks = product.internal_tasks || [];

  return (
    <Card className="border" data-testid={`product-card-${product.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            <div>
              <CardTitle className="text-lg">{product.name}</CardTitle>
              <CardDescription>
                {product.sub_products?.length || 0} sub-products &middot; {extTasks.length} rep tasks &middot; {intTasks.length} internal tasks
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} data-testid={`edit-${product.id}`}>
              <PenLine className="mr-1 h-3 w-3" />Edit
            </Button>
            <Button variant="ghost" size="icon" className="text-destructive" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Sub-products */}
          {product.sub_products?.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Sub-Products</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {product.sub_products.map((sp) => (
                  <Badge key={sp.id} variant="outline">{sp.name}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* External Tasks */}
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Rep Tasks (External)</Label>
            <div className="space-y-1 mt-1">
              {extTasks.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm p-1.5 rounded bg-secondary/50">
                  <Badge variant="outline" className="text-[10px] h-4">{t.assignee_role || "rep"}</Badge>
                  <span>{t.title}</span>
                  {t.linked_form && <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-auto">Form: {t.linked_form}</Badge>}
                  {t.default_due_days && <Badge variant="outline" className="text-[10px] ml-auto">{t.default_due_days}d</Badge>}
                </div>
              ))}
            </div>
          </div>

          {/* Internal Tasks */}
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Team Tasks (Internal)</Label>
            <div className="space-y-1 mt-1">
              {intTasks.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm p-1.5 rounded bg-secondary/50">
                  <Badge variant="outline" className="text-[10px] h-4">{t.assignee_role || "rp"}</Badge>
                  <span>{t.title}</span>
                  {t.default_due_days && <Badge variant="outline" className="text-[10px] ml-auto">{t.default_due_days}d</Badge>}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function ProductEditor({ product, onSave, onCancel, saving }) {
  const [form, setForm] = useState(JSON.parse(JSON.stringify(product)));

  const addSubProduct = () => {
    setForm((p) => ({ ...p, sub_products: [...(p.sub_products || []), { id: "", name: "" }] }));
  };
  const removeSubProduct = (i) => {
    setForm((p) => ({ ...p, sub_products: p.sub_products.filter((_, idx) => idx !== i) }));
  };
  const updateSubProduct = (i, field, val) => {
    setForm((p) => {
      const subs = [...p.sub_products];
      subs[i] = { ...subs[i], [field]: val };
      if (field === "name" && !subs[i].id) subs[i].id = val.toLowerCase().replace(/[^a-z0-9]+/g, "_");
      return { ...p, sub_products: subs };
    });
  };

  const addTask = (type) => {
    const key = type === "external" ? "external_tasks" : "internal_tasks";
    setForm((p) => ({
      ...p,
      [key]: [...(p[key] || []), { title: "", assignee_role: type === "external" ? "rep" : "rp", linked_form: "", default_due_days: null }],
    }));
  };
  const removeTask = (type, i) => {
    const key = type === "external" ? "external_tasks" : "internal_tasks";
    setForm((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));
  };
  const updateTask = (type, i, field, val) => {
    const key = type === "external" ? "external_tasks" : "internal_tasks";
    setForm((p) => {
      const tasks = [...p[key]];
      tasks[i] = { ...tasks[i], [field]: val };
      return { ...p, [key]: tasks };
    });
  };

  return (
    <Card className="border border-primary/30 ring-1 ring-primary/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Editing: {form.name}</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel}><X className="mr-1 h-3 w-3" />Cancel</Button>
            <Button size="sm" onClick={() => onSave(form)} disabled={saving} data-testid={`save-${form.id}`}>
              {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Product Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Quantity Label (if needed)</Label><Input value={form.quantity_label || ""} onChange={(e) => setForm({ ...form, quantity_label: e.target.value })} placeholder="e.g., Ad Impressions / Monthly Budget" /></div>
        </div>
        <label className="flex items-center gap-2">
          <Checkbox checked={form.needs_quantity || false} onCheckedChange={(v) => setForm({ ...form, needs_quantity: !!v })} />
          <span className="text-sm">Requires quantity input on orders</span>
        </label>

        {/* Sub-Products */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Sub-Products</Label>
            <Button type="button" variant="outline" size="sm" onClick={addSubProduct}><Plus className="mr-1 h-3 w-3" />Add</Button>
          </div>
          <div className="space-y-2">
            {(form.sub_products || []).map((sp, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={sp.name} onChange={(e) => updateSubProduct(i, "name", e.target.value)} placeholder="Product name" className="flex-1" />
                <Input value={sp.id} onChange={(e) => updateSubProduct(i, "id", e.target.value)} placeholder="ID (auto)" className="w-32 text-xs" />
                <Button variant="ghost" size="icon" onClick={() => removeSubProduct(i)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </div>

        {/* External Tasks */}
        <TaskListEditor
          title="Rep Tasks (External)"
          tasks={form.external_tasks || []}
          type="external"
          onAdd={() => addTask("external")}
          onRemove={(i) => removeTask("external", i)}
          onUpdate={(i, f, v) => updateTask("external", i, f, v)}
        />

        {/* Internal Tasks */}
        <TaskListEditor
          title="Team Tasks (Internal)"
          tasks={form.internal_tasks || []}
          type="internal"
          onAdd={() => addTask("internal")}
          onRemove={(i) => removeTask("internal", i)}
          onUpdate={(i, f, v) => updateTask("internal", i, f, v)}
        />
      </CardContent>
    </Card>
  );
}

function TaskListEditor({ title, tasks, type, onAdd, onRemove, onUpdate }) {
  // Build list of all task titles for dependency dropdown
  const allTitles = tasks.map((t) => t.title).filter(Boolean);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-semibold">{title}</Label>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}><Plus className="mr-1 h-3 w-3" />Add Task</Button>
      </div>
      <div className="space-y-2">
        {tasks.map((task, i) => (
          <div key={i} className="flex items-start gap-2 p-2 rounded border bg-secondary/30">
            <div className="flex-1 space-y-2">
              <Input value={task.title} onChange={(e) => onUpdate(i, "title", e.target.value)} placeholder="Task title" className="text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={task.assignee_role || (type === "external" ? "rep" : "rp")} onValueChange={(v) => onUpdate(i, "assignee_role", v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rep">Rep</SelectItem>
                    <SelectItem value="rp">RP (Internal)</SelectItem>
                    <SelectItem value="va">VA</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={task.linked_form || ""} onValueChange={(v) => onUpdate(i, "linked_form", v === "none" ? null : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No form" /></SelectTrigger>
                  <SelectContent>
                    {FORM_OPTIONS.map((f) => <SelectItem key={f.value || "none"} value={f.value || "none"}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Select value={task.depends_on || ""} onValueChange={(v) => onUpdate(i, "depends_on", v === "none" ? null : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No dependency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No dependency</SelectItem>
                    {allTitles.filter((t) => t !== task.title).map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">{t.length > 40 ? t.slice(0, 40) + "..." : t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Input type="number" min="1" value={task.days_after_dependency || ""} onChange={(e) => onUpdate(i, "days_after_dependency", e.target.value ? parseInt(e.target.value) : null)} placeholder="Days after" className="h-8 text-xs" />
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">days after dep</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input type="number" min="1" value={task.default_due_days || ""} onChange={(e) => onUpdate(i, "default_due_days", e.target.value ? parseInt(e.target.value) : null)} placeholder="Days" className="h-8 text-xs" />
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">days from order</span>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="shrink-0 mt-1" onClick={() => onRemove(i)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewProductForm({ onSave, saving }) {
  const [form, setForm] = useState({ id: "", name: "", sub_products: [], needs_quantity: false, quantity_label: "", external_tasks: [], internal_tasks: [] });

  return (
    <div className="space-y-4">
      <div><Label>Product ID</Label><Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="e.g., marketing" /></div>
      <div><Label>Product Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Marketing Products" /></div>
      <Button onClick={() => onSave(form)} disabled={saving || !form.id || !form.name} className="w-full" data-testid="create-product-btn">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Create Product Category
      </Button>
      <p className="text-xs text-muted-foreground">You can add sub-products and tasks after creating the category.</p>
    </div>
  );
}

function StaleTaskCleanupPanel() {
  // Dry-run first so admin sees the count before committing. Second click
  // runs the live cleanup; a third click ("Undo") restores everything.
  const [preview, setPreview] = useState(null);   // dry-run response
  const [applied, setApplied] = useState(null);   // live-run response
  const [busy, setBusy] = useState(false);

  const runDry = async () => {
    setBusy(true);
    try {
      const { data } = await cleanupMonthlyActiveTasks(true);
      setPreview(data);
      setApplied(null);
      if (!data.would_close) toast.success("Nothing stale to clean up.");
    } catch (e) {
      toast.error("Preview failed — check your session.");
    } finally { setBusy(false); }
  };

  const runLive = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await cleanupMonthlyActiveTasks(false);
      setApplied(data);
      setPreview(null);
      toast.success(`Closed ${data.closed} stale tasks. Undo available.`);
    } catch (e) {
      toast.error("Cleanup failed.");
    } finally { setBusy(false); }
  };

  const runUndo = async () => {
    setBusy(true);
    try {
      const { data } = await undoCleanupMonthlyActiveTasks();
      toast.success(`Restored ${data.restored} tasks.`);
      setApplied(null);
      setPreview(null);
    } catch (e) {
      toast.error("Undo failed.");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="stale-task-cleanup-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-900 text-lg">
          <Sparkles className="h-5 w-5" /> Clean up stale monthly-active tasks
        </CardTitle>
        <CardDescription className="text-amber-900/70">
          Closes every pending / in-progress task sitting on a client who has already moved to Monthly Active. Day-to-day work for those clients is handled via messages, so the leftover checklist just inflates counters. Fully reversible.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={runDry}
            disabled={busy}
            data-testid="cleanup-preview-btn"
          >
            {busy && !preview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Preview cleanup
          </Button>
          {preview && preview.would_close > 0 && (
            <Button
              onClick={runLive}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="cleanup-apply-btn"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Close {preview.would_close} stale task{preview.would_close === 1 ? "" : "s"}
            </Button>
          )}
          {applied && applied.closed > 0 && (
            <Button
              variant="outline"
              onClick={runUndo}
              disabled={busy}
              data-testid="cleanup-undo-btn"
            >
              <Undo2 className="mr-2 h-4 w-4" />
              Undo (restore {applied.closed})
            </Button>
          )}
        </div>

        {preview && (
          <div className="text-xs text-amber-900 bg-white/60 border border-amber-200 rounded px-3 py-2" data-testid="cleanup-preview-result">
            Dry run: <strong>{preview.would_close}</strong> tasks would close across <strong>{preview.affected_projects}</strong> monthly-active project{preview.affected_projects === 1 ? "" : "s"} (internal: {preview.internal}, external: {preview.external}).
            {preview.would_close === 0 && " Nothing to do."}
          </div>
        )}
        {applied && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="cleanup-applied-result">
            Closed <strong>{applied.closed}</strong> tasks ({applied.closed_pending} pending, {applied.closed_in_progress} in-progress). Undo above will restore each task to its previous status.
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function TaskTemplateRetrofitPanel() {
  // Re-aligns pending tasks on ACTIVE projects to the current (shorter) Feb 2026
  // templates. Safe: preserves any task that has an assignee, comments, or progress —
  // only untouched pending tasks whose title isn't in the current template are retired.
  // Missing template tasks are added. Fully reversible via the undo button.
  const [preview, setPreview] = React.useState(null);
  const [applied, setApplied] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const runDry = async () => {
    setBusy(true);
    try {
      const { data } = await retrofitTaskTemplates(true);
      setPreview(data);
      setApplied(null);
      if (!data.would_delete && !data.would_add) toast.success("Everything's already aligned.");
    } catch (e) {
      toast.error("Preview failed — check your session.");
    } finally { setBusy(false); }
  };

  const runLive = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await retrofitTaskTemplates(false);
      setApplied(data);
      setPreview(null);
      toast.success(`Retrofit done: -${data.deleted} +${data.added} tasks. Undo available.`);
    } catch (e) {
      toast.error("Retrofit failed.");
    } finally { setBusy(false); }
  };

  const runUndo = async () => {
    setBusy(true);
    try {
      const { data } = await undoRetrofitTaskTemplates();
      toast.success(`Undo: restored ${data.restored}, removed ${data.removed_bulk_added} added tasks.`);
      setApplied(null);
      setPreview(null);
    } catch (e) {
      toast.error("Undo failed.");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-sky-200 bg-sky-50/40" data-testid="task-template-retrofit-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sky-900 text-lg">
          <Sparkles className="h-5 w-5" /> Re-align pending tasks to the current template
        </CardTitle>
        <CardDescription className="text-sky-900/70">
          Applies the current (Feb 2026 revised) shorter task lists to every ACTIVE project.
          Safe by design: any task with an assignee, comments, or that's already in progress is preserved.
          Only untouched pending tasks whose title isn't in the current template are retired, and missing template tasks are added. Fully reversible.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={runDry}
            disabled={busy}
            data-testid="retrofit-preview-btn"
          >
            {busy && !preview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Preview retrofit
          </Button>
          {preview && (preview.would_delete > 0 || preview.would_add > 0) && (
            <Button
              onClick={runLive}
              disabled={busy}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="retrofit-apply-btn"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Retire {preview.would_delete} · Add {preview.would_add}
            </Button>
          )}
          {applied && (applied.deleted > 0 || applied.added > 0) && (
            <Button
              variant="outline"
              onClick={runUndo}
              disabled={busy}
              data-testid="retrofit-undo-btn"
            >
              <Undo2 className="mr-2 h-4 w-4" />
              Undo
            </Button>
          )}
        </div>

        {preview && (
          <div className="text-xs text-sky-900 bg-white/60 border border-sky-200 rounded px-3 py-2 space-y-1" data-testid="retrofit-preview-result">
            <div>
              Dry run: <strong>-{preview.would_delete}</strong> tasks to retire, <strong>+{preview.would_add}</strong> to add,
              across <strong>{preview.projects_touched}</strong> of {preview.projects_scanned} active projects.
              {preview.would_delete === 0 && preview.would_add === 0 && " Nothing to do."}
            </div>
            {preview.sample_changes?.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-sky-900/80">Sample changes ({preview.sample_changes.length})</summary>
                <ul className="mt-1 ml-4 list-disc space-y-1 text-sky-900/80">
                  {preview.sample_changes.map((s, i) => (
                    <li key={i}>
                      <div className="font-mono text-[10px] text-sky-900/60">{s.project_id} · {(s.product_types || []).join(", ")}</div>
                      {s.delete_titles?.length > 0 && <div>− {s.delete_titles.join(", ")}</div>}
                      {s.add_titles?.length > 0 && <div>+ {s.add_titles.join(", ")}</div>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {applied && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="retrofit-applied-result">
            Retrofit applied: <strong>-{applied.deleted}</strong> retired, <strong>+{applied.added}</strong> added
            across <strong>{applied.projects_touched}</strong> projects. Undo above restores everything.
          </div>
        )}
      </CardContent>
    </Card>
  );
}


